import { FormCreator, FormCreatorProps, FormFieldDef } from "@/components/shared/form-creator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SurveyQuestionDraft } from "@/models/survey.model";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dispatch, SetStateAction, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface AddSurveyModalProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  onSubmit: (values: SurveyQuestionDraft) => void;
}

const formSchema = z.object({
  question: z.string().min(1, "Question is required"),
  answer_type: z.string().min(1, "Answer Type is required"),
  options: z.string().min(1, "Options is required").optional(),
});

type FormValues = z.infer<typeof formSchema>;

export default function AddSurveyModal({ open, setOpen, onSubmit }: AddSurveyModalProps) {
  const [loading, setLoading] = useState(false);
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      question: "",
      answer_type: "",
    },
  });

  const fields: FormFieldDef[] = [
    { type: "text", name: "question", label: "Question", required: true },
    {
      type: "select",
      name: "answer_type",
      label: "Answer Type",
      required: true,
      options: [
        { label: "Text", value: "text" },
        { label: "Yes/No", value: "boolean" },
        { label: "Multiple Choice", value: "select" },
      ],
    },
    {
      type: "text",
      name: "options",
      label: "Options",
      description: "Please write options separated by comma(,)",
      hidden: (vals) => vals.answer_type !== "select",
    },
  ];

  const props: FormCreatorProps = {
    form,
    onSubmit: async (values: FormValues) => {
      setLoading(true);
      try {
        onSubmit({ question: values.question, answer_type: values.answer_type, options: values.options });
        form.reset();
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    },
    formDefinition: fields,
    buttonText: loading ? "Creating..." : "Create Item",
    resetButtonText: "Reset",
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <form>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add Survey Question</DialogTitle>
          </DialogHeader>

          <FormCreator {...props} />
        </DialogContent>
      </form>
    </Dialog>
  );
}
